import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [activeForm, setActiveForm] = useState('contact'); // contact or volunteer
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  // Volunteer form state
  const [volunteerForm, setVolunteerForm] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    organization: '',
    message: '',
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccess(null);

    try {
      await axios.post(`${API_URL}/api/contact`, contactForm);
      setSuccess('contact');
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccess(null);

    try {
      await axios.post(`${API_URL}/api/volunteer`, volunteerForm);
      setSuccess('volunteer');
      setVolunteerForm({ name: '', email: '', phone: '', interest: '', organization: '', message: '' });
    } catch (error) {
      console.error('Volunteer form error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info - Email Only */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1E3A8A] mb-4">
              Get In Touch
            </p>
            <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] tracking-tight mb-6">
              Let's Connect
            </h2>
            <p className="text-lg text-slate-600 font-light mb-12 leading-relaxed">
              Have questions about our mission or want to get involved? 
              We'd love to hear from you. Reach out and join our movement.
            </p>

            {/* Email Contact Only */}
            <motion.div
              className="flex items-start gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 rounded-xl bg-[#DBEAFE] flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-[#1E3A8A]" />
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Email</p>
                <a 
                  href="mailto:Info@prestigioustravelclub.org"
                  className="text-xl text-slate-800 font-medium hover:text-[#1E3A8A] transition-colors"
                >
                  Info@prestigioustravelclub.org
                </a>
                <p className="text-sm text-slate-500 mt-2">
                  We typically respond within 24-48 hours
                </p>
              </div>
            </motion.div>

            {/* Social Links */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <p className="text-sm text-slate-500 mb-4">Follow Us</p>
              <div className="flex gap-4">
                <a 
                  href="https://www.facebook.com/prestigioustravelclub" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-[#1E3A8A] hover:text-white transition-colors text-slate-600"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.youtube.com/@PrestigiousTravelClub" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-[#1E3A8A] hover:text-white transition-colors text-slate-600"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Forms */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="form-card">
              {/* Form Tabs */}
              <div className="flex gap-4 mb-8 border-b border-slate-200">
                <button
                  onClick={() => { setActiveForm('contact'); setSuccess(null); }}
                  className={`pb-4 px-2 font-medium transition-colors relative ${
                    activeForm === 'contact' 
                      ? 'text-[#1E3A8A]' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="contact-tab"
                >
                  Contact Us
                  {activeForm === 'contact' && (
                    <motion.div 
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1E3A8A]"
                      layoutId="activeTab"
                    />
                  )}
                </button>
                <button
                  onClick={() => { setActiveForm('volunteer'); setSuccess(null); }}
                  className={`pb-4 px-2 font-medium transition-colors relative ${
                    activeForm === 'volunteer' 
                      ? 'text-[#1E3A8A]' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="volunteer-tab"
                >
                  Partner / Volunteer
                  {activeForm === 'volunteer' && (
                    <motion.div 
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1E3A8A]"
                      layoutId="activeTab"
                    />
                  )}
                </button>
              </div>

              {/* Success Message */}
              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-green-700">
                    {success === 'contact' 
                      ? 'Thank you for reaching out! We\'ll be in touch soon.'
                      : 'Thank you for your interest! We\'ll contact you about opportunities.'
                    }
                  </p>
                </motion.div>
              )}

              {/* Contact Form */}
              {activeForm === 'contact' && (
                <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                      <Input
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        placeholder="Your name"
                        className="border-slate-200 focus:ring-[#1E3A8A] focus:border-[#1E3A8A]"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                      <Input
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        placeholder="your@email.com"
                        className="border-slate-200 focus:ring-[#1E3A8A] focus:border-[#1E3A8A]"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Subject *</label>
                    <Input
                      required
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                      placeholder="How can we help?"
                      className="border-slate-200 focus:ring-[#1E3A8A] focus:border-[#1E3A8A]"
                      data-testid="contact-subject"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                    <Textarea
                      required
                      rows={4}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      placeholder="Tell us more..."
                      className="border-slate-200 focus:ring-[#1E3A8A] focus:border-[#1E3A8A]"
                      data-testid="contact-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white py-6 rounded-xl flex items-center justify-center gap-2"
                    data-testid="contact-submit"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Volunteer Form */}
              {activeForm === 'volunteer' && (
                <form onSubmit={handleVolunteerSubmit} className="space-y-6" data-testid="volunteer-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                      <Input
                        required
                        value={volunteerForm.name}
                        onChange={(e) => setVolunteerForm({...volunteerForm, name: e.target.value})}
                        placeholder="Your name"
                        className="border-slate-200 focus:ring-[#1E3A8A] focus:border-[#1E3A8A]"
                        data-testid="volunteer-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                      <Input
                        type="email"
                        required
                        value={volunteerForm.email}
                        onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})}
                        placeholder="your@email.com"
                        className="border-slate-200 focus:ring-[#1E3A8A] focus:border-[#1E3A8A]"
                        data-testid="volunteer-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone *</label>
                      <Input
                        type="tel"
                        required
                        value={volunteerForm.phone}
                        onChange={(e) => setVolunteerForm({...volunteerForm, phone: e.target.value})}
                        placeholder="(555) 555-5555"
                        className="border-slate-200 focus:ring-[#1E3A8A] focus:border-[#1E3A8A]"
                        data-testid="volunteer-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">I'm interested in *</label>
                      <Select 
                        value={volunteerForm.interest} 
                        onValueChange={(value) => setVolunteerForm({...volunteerForm, interest: value})}
                      >
                        <SelectTrigger className="border-slate-200 focus:ring-[#1E3A8A]" data-testid="volunteer-interest">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="volunteer">Volunteering</SelectItem>
                          <SelectItem value="partner">Corporate Partnership</SelectItem>
                          <SelectItem value="sponsor">Sponsorship</SelectItem>
                          <SelectItem value="media">Media Inquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Organization (if applicable)</label>
                    <Input
                      value={volunteerForm.organization}
                      onChange={(e) => setVolunteerForm({...volunteerForm, organization: e.target.value})}
                      placeholder="Company or organization name"
                      className="border-slate-200 focus:ring-[#1E3A8A] focus:border-[#1E3A8A]"
                      data-testid="volunteer-organization"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Tell us about yourself *</label>
                    <Textarea
                      required
                      rows={4}
                      value={volunteerForm.message}
                      onChange={(e) => setVolunteerForm({...volunteerForm, message: e.target.value})}
                      placeholder="Share your background and how you'd like to contribute..."
                      className="border-slate-200 focus:ring-[#1E3A8A] focus:border-[#1E3A8A]"
                      data-testid="volunteer-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white py-6 rounded-xl flex items-center justify-center gap-2"
                    data-testid="volunteer-submit"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Submit Application
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-400 leading-relaxed mt-4" data-testid="sms-disclosure-volunteer">
                    By providing your phone number and submitting this form, you consent to receive SMS messages from
                    Prestigious Travel Club Inc. Message frequency may vary. Message and data rates may apply.
                    Reply STOP to opt out. Reply HELP for help. Consent is not a condition of purchase.
                    See our <a href="/privacy-policy" className="underline hover:text-[#1E3A8A]">Privacy Policy</a> and <a href="/terms-and-conditions" className="underline hover:text-[#1E3A8A]">Terms & Conditions</a>.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
